import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { CorrectionMove, GameCorrections } from '../types/CorrectionModel';
import { MoveType } from '../types/MoveType';
import CorrectionsAPI from '../api/CorrectionsAPI';
import { CorrectionOutcome } from './CorrectionBoard';
import AppContext from '../context/AppContext';
import { awardForAttempt, syncWalletFromServer } from '../utils/earnings';
import { useAppSelector } from '../store';

export type CorrectionMoveState = CorrectionMove & {
  effectiveSolved: boolean;
  // Repairs on record plus the ones earned in this session — what the move list counts.
  effectiveRepairCount: number;
  // The repair count the payout ladder reads: RepairCount after time recovery, plus this session's.
  effectivePayoutPriors: number;
};

// Category-based score: blunders + misses are worth 50%, mistakes 30%, inaccuracies 20%, each
// normalised over the categories that occur, so a game can reach 100 by fixing what it has. 100 when
// there's nothing to correct. Mirrors CorrectionsService.CategoryScore.
function categoryScore(mistakes: CorrectionMoveState[]): number {
  const weights = [50, 30, 20];
  const total = [0, 0, 0];
  const done = [0, 0, 0];
  for (const m of mistakes) {
    const cat =
      m.MoveType === MoveType.Blunder || m.MoveType === MoveType.Miss
        ? 0
        : m.MoveType === MoveType.Mistake
          ? 1
          : m.MoveType === MoveType.Inaccuracy
            ? 2
            : -1;
    if (cat < 0) continue;
    total[cat] += 1;
    if (m.effectiveSolved) done[cat] += 1;
  }
  let num = 0;
  let den = 0;
  for (let c = 0; c < 3; c++) {
    if (total[c] === 0) continue;
    num += weights[c] * (done[c] / total[c]);
    den += weights[c];
  }
  return den === 0 ? 100 : Math.round((100 * num) / den);
}

// The three repairable categories. Misses ride with blunders: they share the 50% tier.
export type MistakeCategory = 'blunder' | 'mistake' | 'inaccuracy';
const CATEGORY_ORDER: MistakeCategory[] = ['inaccuracy', 'mistake', 'blunder'];
const CATEGORY_LABEL: Record<MistakeCategory, string> = {
  blunder: 'Blunders',
  mistake: 'Mistakes',
  inaccuracy: 'Inaccuracies',
};
// Each category's representative move type, for its icon and colour.
const CATEGORY_TYPE: Record<MistakeCategory, MoveType> = {
  blunder: MoveType.Blunder,
  mistake: MoveType.Mistake,
  inaccuracy: MoveType.Inaccuracy,
};
export function categoryOf(mt: MoveType): MistakeCategory {
  if (mt === MoveType.Blunder || mt === MoveType.Miss) return 'blunder';
  if (mt === MoveType.Mistake) return 'mistake';
  return 'inaccuracy';
}

// Severity order the repair flow advances through, worst first (see PracticeProvider).
export const CATEGORY_PRIORITY: MistakeCategory[] = ['blunder', 'mistake', 'inaccuracy'];

// Categories disabled by default: everything except the worst tier present.
function defaultDisabled(mistakes: { MoveType: MoveType }[]): Set<MistakeCategory> {
  const present = new Set(mistakes.map((m) => categoryOf(m.MoveType)));
  const priority: MistakeCategory[] = ['blunder', 'mistake', 'inaccuracy'];
  const chosen = priority.find((c) => present.has(c));
  return new Set(priority.filter((c) => c !== chosen));
}

// One category's checkbox: name, representative type, count, and whether it's selected.
export type CategoryInfo = {
  key: MistakeCategory;
  label: string;
  moveType: MoveType;
  count: number;
  enabled: boolean;
};

type CorrectionsContextType = {
  loading: boolean;
  error: boolean;
  mistakes: CorrectionMoveState[]; // every mistake (for the category counts)
  activeMistakes: CorrectionMoveState[]; // only the selected categories — what practice + score use
  categories: CategoryInfo[]; // the category picker, present categories only, worst-first
  toggleCategory: (key: MistakeCategory) => void;
  // Turn a category on (idempotent), for the repair progression.
  enableCategory: (key: MistakeCategory) => void;
  score: number; // live 0..100, over the selected categories
  hasMistakes: boolean;
  solvedCount: number; // over the selected categories
  totalCount: number; // over the selected categories
  totalWeight: number; // sum of all mistake weights, the denominator of each mistake's share
  // Attempt results this session, keyed by move id (drives the reveal and board lock).
  outcomes: Record<number, CorrectionOutcome>;
  recordOutcome: (mistake: CorrectionMove, outcome: CorrectionOutcome) => void;
  clearOutcome: (moveId: number) => void;
  // Whether the current attempt at a move took the paid hint. The button is in PracticeBelowBoard but
  // the payout is applied here.
  markHintUsed: (moveId: number, used: boolean) => void;
  reload: () => void;
};

const CorrectionsContext = createContext<CorrectionsContextType>({
  loading: false,
  error: false,
  mistakes: [],
  activeMistakes: [],
  categories: [],
  toggleCategory: () => {},
  enableCategory: () => {},
  score: 100,
  hasMistakes: false,
  solvedCount: 0,
  totalCount: 0,
  totalWeight: 0,
  outcomes: {},
  recordOutcome: () => {},
  clearOutcome: () => {},
  markHintUsed: () => {},
  reload: () => {},
});

export function useCorrections(): CorrectionsContextType {
  return useContext(CorrectionsContext);
}

// One game's "correct your mistakes" state: the viewer's mistakes and score, tracked live as they
// solve. Signed-in solves persist to the CorrectionAttempt store; guests solve in-session only. The
// header gauge and the correction overlay both read from here.
export default function CorrectionsProvider(props: {
  gameId: number;
  enabled: boolean; // only evaluated games have reliable mistakes to correct
  children: React.ReactNode;
}): React.ReactNode {
  const { gameId, enabled, children } = props;
  const { isAuthenticated } = useContext(AppContext);
  const username = useAppSelector((state) => state.paramsReducer.params.username);
  const gameRefreshKey = useAppSelector((state) => state.uiReducer.gameRefreshKey);

  const [data, setData] = useState<GameCorrections | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState(false);
  // Move ids solved this session (optimistic for signed-in users, the only record for guests).
  const [sessionSolved, setSessionSolved] = useState<Set<number>>(new Set());
  // Successful repairs since load, added on top of the server's RepairCount snapshot.
  const [sessionRepairs, setSessionRepairs] = useState<Record<number, number>>({});
  const [outcomes, setOutcomes] = useState<Record<number, CorrectionOutcome>>({});
  // Move ids whose current attempt used the paid hint (halves the payout).
  const [hinted, setHinted] = useState<Record<number, boolean>>({});
  // Categories switched off in the picker, held as the disabled set so a new category starts selected.
  const [disabled, setDisabled] = useState<Set<MistakeCategory>>(new Set());
  const reqRef = useRef(0);

  const load = useCallback(async () => {
    if (!enabled) {
      setData(null);
      setLoading(false);
      return;
    }
    const req = ++reqRef.current;
    setLoading(true);
    setError(false);
    try {
      const result = await CorrectionsAPI.getForGame(gameId, username);
      if (reqRef.current !== req) return;
      setData(result);
      setSessionSolved(new Set());
      setSessionRepairs({});
      setOutcomes({});
      setHinted({});
      // Default selection: the worst tier the game has.
      setDisabled(defaultDisabled(result.Mistakes ?? []));
    } catch {
      if (reqRef.current !== req) return;
      setError(true);
      setData(null);
    } finally {
      if (reqRef.current === req) setLoading(false);
    }
  }, [gameId, username, enabled, gameRefreshKey]);

  useEffect(() => {
    load();
  }, [load]);

  const recordOutcome = useCallback(
    (mistake: CorrectionMove, outcome: CorrectionOutcome) => {
      setOutcomes((prev) => ({ ...prev, [mistake.MoveId]: outcome }));

      // Pay out before the auth check: guests earn into a tab-local balance. priorSuccesses is the server's
      // time-discounted count plus this session's repairs.
      const hintUsed = hinted[mistake.MoveId] === true;
      awardForAttempt({
        success: outcome.correct,
        swingPoints: mistake.PlayedSwing,
        priorSuccesses: mistake.PayoutPriorSuccesses + (sessionRepairs[mistake.MoveId] ?? 0),
        hintUsed,
        // A wrong answer costs what this move gives up; null when ungraded (the mistake's swing then).
        lossPoints: outcome.dropPct,
        // The board the repair happened on, for the payout chip; null makes it rise from the counter.
        anchor: document.querySelector('.cm-correct-board-wrap, .cm-board-frame') ?? null,
      });

      if (outcome.correct) {
        setSessionSolved((prev) => {
          const next = new Set(prev);
          next.add(mistake.MoveId);
          return next;
        });
        setSessionRepairs((prev) => ({
          ...prev,
          [mistake.MoveId]: (prev[mistake.MoveId] ?? 0) + 1,
        }));
      }
      // Persist for signed-in users only; guests keep it to the session.
      if (isAuthenticated) {
        CorrectionsAPI.recordAttempt(
          mistake.MoveId,
          mistake.GameId,
          outcome.correct,
          hintUsed,
          outcome.dropPct,
        )
          .then(syncWalletFromServer)
          .catch(() => {
            /* progress tracking is best-effort; the attempt already resolved on screen */
          });
      }
    },
    [isAuthenticated, sessionRepairs, hinted],
  );

  const markHintUsed = useCallback((moveId: number, used: boolean) => {
    setHinted((prev) => {
      if ((prev[moveId] === true) === used) return prev;
      const next = { ...prev };
      if (used) next[moveId] = true;
      else delete next[moveId];
      return next;
    });
  }, []);

  const clearOutcome = useCallback((moveId: number) => {
    setOutcomes((prev) => {
      if (!(moveId in prev)) return prev;
      const next = { ...prev };
      delete next[moveId];
      return next;
    });
  }, []);

  const toggleCategory = useCallback((key: MistakeCategory) => {
    setDisabled((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const enableCategory = useCallback((key: MistakeCategory) => {
    setDisabled((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const value = useMemo<CorrectionsContextType>(() => {
    const raw = data?.Mistakes ?? [];
    const mistakes: CorrectionMoveState[] = raw
      .map((m) => ({
        ...m,
        effectiveSolved: m.Solved || sessionSolved.has(m.MoveId),
        effectiveRepairCount: (m.RepairCount ?? 0) + (sessionRepairs[m.MoveId] ?? 0),
        // What the payout is priced on, same shape: the discounted count plus this session's repairs.
        effectivePayoutPriors: (m.PayoutPriorSuccesses ?? 0) + (sessionRepairs[m.MoveId] ?? 0),
      }))
      // In move order, so the list reads like the game.
      .sort((a, b) => a.Number - b.Number || a.Color - b.Color);
    // Per-category counts, then the picker rows (present categories only, worst-first).
    const counts = new Map<MistakeCategory, number>();
    for (const m of mistakes) {
      const c = categoryOf(m.MoveType);
      counts.set(c, (counts.get(c) ?? 0) + 1);
    }
    const categories: CategoryInfo[] = CATEGORY_ORDER.filter((c) => counts.has(c)).map((c) => ({
      key: c,
      label: CATEGORY_LABEL[c],
      moveType: CATEGORY_TYPE[c],
      count: counts.get(c)!,
      enabled: !disabled.has(c),
    }));

    // Practice + score operate on the selected categories only.
    const activeMistakes = mistakes.filter((m) => !disabled.has(categoryOf(m.MoveType)));
    let total = 0;
    let solvedCount = 0;
    for (const m of activeMistakes) {
      total += m.Weight;
      if (m.effectiveSolved) {
        solvedCount += 1;
      }
    }
    const score = categoryScore(activeMistakes);
    return {
      loading,
      error,
      mistakes,
      activeMistakes,
      categories,
      toggleCategory,
      enableCategory,
      score,
      hasMistakes: mistakes.length > 0,
      solvedCount,
      totalCount: activeMistakes.length,
      totalWeight: total,
      outcomes,
      recordOutcome,
      clearOutcome,
      markHintUsed,
      reload: load,
    };
  }, [
    data,
    sessionSolved,
    sessionRepairs,
    outcomes,
    disabled,
    loading,
    error,
    recordOutcome,
    clearOutcome,
    markHintUsed,
    toggleCategory,
    enableCategory,
    load,
  ]);

  return <CorrectionsContext.Provider value={value}>{children}</CorrectionsContext.Provider>;
}
